import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import moment from 'moment';
import { concat, EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { get_reports, get_reports_success } from '@rpt-ngrx/actions/reports.action';
import { add_sites_success } from '@rpt-ngrx/actions/sites.action';
import {
    CustomReportsService,
    OpraDataService,
    OpraSessionService,
    PermissionService,
    PreferencesService,
    TokenSessionService,
} from '@shared/services';

import { IFRAME_MODE } from '../../routes';
import { set_company_preferences } from '../actions/company.action';
import {
    check_user_auth,
    check_user_auth_failure,
    check_user_auth_success,
    fetch_companies,
    fetch_companies_failure,
    fetch_companies_success,
    get_access_token,
    get_access_token_failure,
    get_access_token_for_no_iframe,
    get_access_token_for_no_iframe_failure,
    get_access_token_for_no_iframe_success,
    get_access_token_success,
    get_company_lang,
    get_company_roles,
    get_company_roles_failure,
    get_company_roles_success,
    get_permissions,
    get_permissions_failure,
    get_permissions_success,
    get_user_failure,
    get_user_success,
    init_company_session,
    local_check_user_auth,
    local_check_user_auth_failure,
    local_check_user_auth_success,
    log_out,
    log_out_success,
    restore_token,
    restore_token_success,
    set_session_ready,
    user_login,
    user_login_failure,
    user_login_success,
} from '../actions/session.action';

@Injectable()
export class SessionEffects {
    constructor(
        private actions$: Actions,
        private permissionService: PermissionService,
        private tokenSessionService: TokenSessionService,
        private opraDataService: OpraDataService,
        private opraSessionService: OpraSessionService,
        private reportsService: CustomReportsService,
        private preferencesService: PreferencesService,
    ) {}

    getCompaniesWithSites$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fetch_companies),
            mergeMap(() =>
                this.opraDataService.fetchCompaniesWithSites().pipe(
                    map(({ items }) => {
                        return fetch_companies_success({ items });
                    }),
                    catchError(async () => fetch_companies_failure),
                ),
            ),
        );
    });

    getAccessToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_access_token, check_user_auth_success),
            mergeMap(({ companyId }) =>
                this.opraSessionService.receiveAccessToken(companyId).pipe(
                    map(({ token }) => {
                        const externalLogin = (location.hash || '').startsWith(`#/${IFRAME_MODE}`);
                        if (!externalLogin) {
                            localStorage.setItem('global/AUTH_KEY', token.toString());
                        }
                        this.tokenSessionService.saveToken({ token: token });
                        return get_access_token_success({ token });
                    }),
                    catchError(async () => get_access_token_failure),
                ),
            ),
        );
    });

    getAccessTokenFoNoIframe$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_access_token_for_no_iframe),
            mergeMap(({ companyId }) =>
                this.opraSessionService.receiveAccessToken(companyId).pipe(
                    mergeMap(({ token }) =>
                        this.opraDataService.getUserInfo().pipe(
                            map((user) => {
                                localStorage.setItem('user', JSON.stringify(user));
                                this.tokenSessionService.saveToken({ token: token });
                                return get_access_token_for_no_iframe_success({ token, companyId, user });
                            }),
                        ),
                    ),
                    catchError(async () => get_access_token_for_no_iframe_failure),
                ),
            ),
        );
    });

    getAccessTokenFoNoIframeSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_access_token_for_no_iframe_success),
            map(({ companyId }) => {
                this.reportsService.setCompanyId(companyId);
                return set_company_preferences({ id: companyId });
            }),
        );
    });

    initCompanySession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(init_company_session),
            mergeMap(({ companyId, user }) =>
                concat(of(get_company_lang({ companyId })), of(get_company_roles({ companyId, user }))),
            ),
        );
    });

    getUserCompanyRoles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_access_token_for_no_iframe_success, get_user_success),
            mergeMap(({ companyId, user }) =>
                this.preferencesService.getCompanyRoles(companyId, user.id).pipe(
                    map(({ roles }) => {
                        return get_company_roles_success({ companyRoles: roles });
                    }),
                    catchError(async () => get_company_roles_failure()),
                ),
            ),
        );
    });

    setCompanyPrefs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_access_token),
            map(({ companyId }) => set_company_preferences({ id: companyId })),
        );
    });

    setCompanyPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(check_user_auth_success),
            map(({ companyId }) => set_company_preferences({ id: companyId })),
        );
    });

    logOut$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(log_out),
            map(() => {
                localStorage.removeItem('user');
                localStorage.removeItem('global/AUTH_KEY');
                return log_out_success();
            }),
        );
    });

    /** 1 - Restore token from portal store **/
    checkUserAuth$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(check_user_auth),
            mergeMap(() =>
                of({ user: localStorage.getItem('user'), token: localStorage.getItem('global/AUTH_KEY') }).pipe(
                    map((data) => {
                        if (!data.user || !data.token) {
                            return check_user_auth_failure(null);
                        }
                        const companyId = OpraSessionService.parseToken(JSON.parse(data.token)).user.company_id;
                        this.reportsService.setCompanyId(companyId);
                        return check_user_auth_success({
                            user: JSON.parse(data.user),
                            token: JSON.parse(data.token),
                            companyId: companyId,
                        });
                    }),
                    catchError(() => EMPTY),
                ),
            ),
        );
    });
    /** 1 - Restore token from local store **/
    localCheckUserAuth$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(local_check_user_auth),
            mergeMap(() =>
                of({ user: localStorage.getItem('user'), token: localStorage.getItem('global/AUTH_KEY') }).pipe(
                    map((data) => {
                        if (!data.user || !data.token) {
                            return local_check_user_auth_failure(null);
                        }
                        data.token = typeof data.token === 'string' ? JSON.parse(data.token.toString()) : data.token;
                        const companyId = OpraSessionService.parseToken(data.token).user.company_id;
                        this.reportsService.setCompanyId(companyId);
                        const user = JSON.parse(data.user);
                        const lang = user.preferredLanguage || localStorage.getItem('global/userLocale');
                        moment.locale(lang);
                        return local_check_user_auth_success({ user: user, token: data.token, companyId: companyId });
                    }),
                    catchError(() => EMPTY),
                ),
            ),
        );
    });
    /** 1 - Restore token from portal **/
    restoreToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(restore_token),
            map(({ token, user }) => {
                const companyId = user.company_id;
                localStorage.setItem('global/AUTH_KEY', token.toString());
                this.tokenSessionService.saveToken({ token: token });
                return restore_token_success({ token, user, companyId });
            }),
        );
    });
    /** 1 - Get token after login **/
    userLogin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(user_login),
            mergeMap(({ user, password }) => {
                return this.opraSessionService.login({ user, password }).pipe(
                    map(({ token }) => {
                        if (token) {
                            localStorage.setItem('global/AUTH_KEY', token.toString());
                            const parsedToken = OpraSessionService.parseToken(token);
                            return user_login_success({
                                token: parsedToken.token,
                                companyId: parsedToken.user.companyId,
                            });
                        } else {
                            return user_login_failure();
                        }
                    }),
                    catchError(() => EMPTY),
                );
            }),
        );
    });

    /** 2 - Fetch companies for local run **/
    userLoginSuccess = createEffect(() => {
        return this.actions$.pipe(
            ofType(user_login_success, local_check_user_auth_success),
            switchMap(() =>
                this.opraDataService.isIframe().pipe(
                    filter((isIframe) => !isIframe),
                    map(() => {
                        return fetch_companies();
                    }),
                ),
            ),
        );
    });
    /** 2 - Set user **/
    onUserVerified$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(restore_token_success, check_user_auth_success),
            mergeMap(({ companyId }) =>
                this.opraDataService.getUserInfo().pipe(
                    map((user) => {
                        localStorage.setItem('user', JSON.stringify(user));
                        this.preferencesService.userLang = user.preferred_language;
                        moment.locale(user.preferred_language || localStorage.getItem('global/userLocale'));
                        return get_user_success({ companyId, user });
                    }),
                ),
            ),
            catchError(async () => get_user_failure),
        );
    });

    /** 3 - Set company preferences on user data set **/
    onUserSet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_user_success),
            map(({ companyId }) => {
                this.reportsService.setCompanyId(companyId);
                return set_company_preferences({ id: companyId });
            }),
            catchError(async () => get_user_failure),
        );
    });

    /** 4 - Get permissions on sites set **/
    onCompanyPreferencesSet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(add_sites_success),
            map(() => get_permissions()),
            catchError(async () => get_permissions_failure),
        );
    });

    /** 5 - Set permissions **/
    getPermission$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_permissions),
            mergeMap(() =>
                this.permissionService.fetchUserPermissions().pipe(
                    map((permissions) => {
                        return get_permissions_success({ permissions });
                    }),
                    catchError(async () => get_permissions_failure),
                ),
            ),
        );
    });

    /** 6 - Get reports **/
    getReports$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_permissions_success),
            map(() => get_reports()),
        );
    });

    /** 7 - Set session ready **/
    onReportsSet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_reports_success),
            map(() => set_session_ready()),
        );
    });
}
